<template>
  <LISAPopover placement="bottom-end" role="dialog">
    <template #trigger="{ triggerAttrs }">
      <LISAButton
        v-bind="triggerAttrs"
        size="sm"
        text
        icon="fas fa-save"
        :aria-label="t('global.save')"
      />
    </template>

    <section class="saved-filter-manager" :class="{ 'saved-filter-manager--compact': compact }">
      <div class="saved-filter-manager__list">
        <section
          v-for="group in filterGroups"
          :key="group.scope"
          class="saved-filter-manager__group"
        >
          <header class="saved-filter-manager__group-header">
            <span>{{ group.label }}</span>
            <InfoTooltip v-if="group.scope === 'board'" :text="boardAdminTooltip" />
          </header>
          <article
            v-for="filter in group.filters"
            :key="filter.id"
            class="saved-filter-manager__row"
            @dragover="allowDrop(group.scope, $event)"
            @drop="dropFilter(group, filter.id)"
          >
            <i
              v-if="canReorder(group.scope)"
              class="saved-filter-manager__drag-handle fas fa-grip-vertical"
              draggable="true"
              aria-hidden="true"
              @dragstart="startReorder(group.scope, filter.id, $event)"
              @dragend="cancelReorder"
            />
            <span v-else />
            <span class="saved-filter-manager__badge">
              <i :class="filter.abbreviation" aria-hidden="true" />
            </span>
            <span class="saved-filter-manager__summary">
              <strong>{{ filter.subtitle }}</strong>
            </span>
            <div v-if="canManage(filter)" class="saved-filter-manager__row-actions">
              <LISAButton
                size="sm"
                text
                variant="neutral"
                icon="fas fa-pencil"
                :aria-label="t('global.edit')"
                @click="editFilter(filter)"
              />
              <LISAButton
                size="sm"
                text
                variant="error"
                icon="fas fa-trash-alt"
                :aria-label="t('global.delete')"
                :loading="deletingId === filter.id"
                @click="confirmDeleteFilter(filter)"
              />
            </div>
          </article>
        </section>
      </div>

      <form class="saved-filter-manager__form" @submit.prevent="saveFilter">
        <div v-if="compact" class="saved-filter-manager__fields">
          <InputRow
            v-model="subtitle"
            name="savedFilterDescription"
            type="text"
            :display-name="t('global.description')"
            :max-length="255"
            required
          >
            <template #prepend>
              <LISAFontAwesomeIconPicker
                v-model="abbreviation"
                :label="
                  t('modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.icon')
                "
                text
                :search-label="t('global.search')"
                :empty-label="t('components.shared.iconSelect.FontAwesomeIconSelect.emptyLabel')"
              />
            </template>
          </InputRow>
        </div>
        <div v-else class="saved-filter-manager__fields">
          <LISAInput v-model="subtitle" :label="t('global.description')" maxlength="255" required>
            <template #prepend>
              <LISAFontAwesomeIconPicker
                v-model="abbreviation"
                :label="
                  t('modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.icon')
                "
                text
                :search-label="t('global.search')"
                :empty-label="t('components.shared.iconSelect.FontAwesomeIconSelect.emptyLabel')"
              />
            </template>
          </LISAInput>
        </div>

        <div v-if="boardId != null" class="saved-filter-manager__scope">
          <LISACheckbox
            :model-value="scope === 'board'"
            :disabled="!hasSQDCAdmin"
            :label="
              t('modules.sqdc.components.eventActionBoard.filter.savedFilters.saveBoardFilter')
            "
            @update:model-value="setBoardScope"
          />
          <InfoTooltip :text="boardVisibilityTooltip" />
        </div>

        <div class="saved-filter-manager__actions">
          <LISAButton
            v-if="editingFilter != null"
            type="button"
            size="sm"
            text
            variant="neutral"
            @click="resetForm"
          >
            {{ t("global.cancel") }}
          </LISAButton>
          <LISAButton
            type="submit"
            size="sm"
            icon="fas fa-save"
            :aria-label="t('global.save')"
            :loading="saving"
            :disabled="!canSubmit"
          >
            {{ t("global.save") }}
          </LISAButton>
        </div>
      </form>
    </section>
  </LISAPopover>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { confirmModal } from "@/components/shared/form/ConfirmationModal";
import {
  LISAButton,
  LISACheckbox,
  LISAFontAwesomeIconPicker,
  LISAInput,
  LISAPopover,
} from "@fi-solutions/lisa-ui";
import {
  type FullEventActionBoardFilterFragment,
  useDeleteEventActionBoardFilterMutation,
  useEventActionBoardFiltersQuery,
  useReorderEventActionBoardFiltersMutation,
  useSaveEventActionBoardFilterMutation,
} from "@/graphql";
import { hasRight } from "@/functions/authorizationState";
import { useTranslation } from "@/language/translation";
import { useGetBoardName, useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";

type SerializedFilter = {
  type: number;
  className: string;
  values: unknown;
};

const props = defineProps<{
  boardId?: number;
  filters: SerializedFilter[];
  compact?: boolean;
}>();

const { t } = useTranslation();
const boardId = computed(() => props.boardId);
const boardName = useGetBoardName(boardId);
const { siteId } = useSpecifiedBoardContext(boardId);
const hasSQDCAdmin = computed(() => hasRight("sqdc.admin", siteId.value));

const { result } = useEventActionBoardFiltersQuery(() => ({ boardId: props.boardId }), {
  fetchPolicy: "cache-and-network",
});
const savedFilters = computed(() => result.value?.EventActionBoardFilters ?? []);
const boardFilters = ref<FullEventActionBoardFilterFragment[]>([]);
const personalFilters = ref<FullEventActionBoardFilterFragment[]>([]);
watch(
  savedFilters,
  (filters) => {
    boardFilters.value = filters.filter(({ boardId: filterBoardId }) => filterBoardId != null);
    personalFilters.value = filters.filter(({ boardId: filterBoardId }) => filterBoardId == null);
  },
  { immediate: true },
);

type FilterScope = "board" | "personal";
type FilterGroup = {
  scope: FilterScope;
  label: string;
  filters: FullEventActionBoardFilterFragment[];
};
const filterGroups = computed<FilterGroup[]>(() => [
  { scope: "board", label: t("global.board").toString(), filters: boardFilters.value },
  {
    scope: "personal",
    label: t("components.event-action.filter.FilterUserQuickActions.MyFilter").toString(),
    filters: personalFilters.value,
  },
]);
const draggedFilter = ref<{ scope: FilterScope; id: number } | null>(null);

const abbreviation = ref("");
const subtitle = ref("");
const scope = ref<"personal" | "board">("personal");
const boardAdminTooltip = computed(() =>
  t("modules.sqdc.components.eventActionBoard.filter.savedFilters.adminRequired", {
    right: "sqdc.admin",
  }).toString(),
);
const boardVisibilityTooltip = computed(() =>
  t("modules.sqdc.components.eventActionBoard.filter.savedFilters.boardVisibility", {
    EABName: boardName.value ?? t("global.board"),
    right: "sqdc.admin",
  }).toString(),
);
const editingFilter = ref<FullEventActionBoardFilterFragment | null>(null);
const deletingId = ref<number | null>(null);

const { mutate: save, loading: saving } = useSaveEventActionBoardFilterMutation({
  refetchQueries: ["EventActionBoardFilters"],
});
const { mutate: remove } = useDeleteEventActionBoardFilterMutation({
  refetchQueries: ["EventActionBoardFilters"],
});
const { mutate: reorder } = useReorderEventActionBoardFiltersMutation({
  refetchQueries: ["EventActionBoardFilters"],
});

const canSubmit = computed(
  () =>
    abbreviation.value.trim().length > 0 &&
    subtitle.value.trim().length > 0 &&
    (editingFilter.value != null || props.filters.length > 0),
);

function canManage(filter: FullEventActionBoardFilterFragment) {
  return filter.boardId == null || hasSQDCAdmin.value;
}

function canReorder(filterScope: FilterScope) {
  return filterScope === "personal" || hasSQDCAdmin.value;
}

async function reorderFilters(group: FilterGroup) {
  if (!canReorder(group.scope)) {
    return;
  }
  await reorder({
    ids: group.filters.map(({ id }) => id),
    boardId: group.scope === "board" ? props.boardId : undefined,
  });
}

function startReorder(filterScope: FilterScope, id: number, event: DragEvent) {
  draggedFilter.value = { scope: filterScope, id };
  if (event.dataTransfer != null) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(id));
  }
}

function allowDrop(filterScope: FilterScope, event: DragEvent) {
  if (draggedFilter.value?.scope !== filterScope) {
    return;
  }
  event.preventDefault();
  if (event.dataTransfer != null) {
    event.dataTransfer.dropEffect = "move";
  }
}

async function dropFilter(group: FilterGroup, targetId: number) {
  const dragged = draggedFilter.value;
  draggedFilter.value = null;
  if (dragged == null || dragged.scope !== group.scope || dragged.id === targetId) {
    return;
  }
  const sourceIndex = group.filters.findIndex(({ id }) => id === dragged.id);
  const targetIndex = group.filters.findIndex(({ id }) => id === targetId);
  if (sourceIndex < 0 || targetIndex < 0) {
    return;
  }
  const [filter] = group.filters.splice(sourceIndex, 1);
  group.filters.splice(targetIndex, 0, filter);
  await reorderFilters(group);
}

function cancelReorder() {
  draggedFilter.value = null;
}

function editFilter(filter: FullEventActionBoardFilterFragment) {
  editingFilter.value = filter;
  abbreviation.value = filter.abbreviation;
  subtitle.value = filter.subtitle;
  scope.value = filter.boardId == null ? "personal" : "board";
}

function resetForm() {
  editingFilter.value = null;
  abbreviation.value = "";
  subtitle.value = "";
  scope.value = "personal";
}

function setBoardScope(boardScope: boolean) {
  scope.value = boardScope && hasSQDCAdmin.value ? "board" : "personal";
}

async function saveFilter() {
  if (!canSubmit.value) {
    return;
  }
  const storedFilters = editingFilter.value?.filters ?? props.filters;
  await save({
    filter: {
      id: editingFilter.value?.id,
      abbreviation: abbreviation.value.trim(),
      subtitle: subtitle.value.trim(),
      boardId: scope.value === "board" ? props.boardId : undefined,
      filters: storedFilters.map(({ type, className, values }) => ({ type, className, values })),
    },
  });
  resetForm();
}

function confirmDeleteFilter(filter: FullEventActionBoardFilterFragment) {
  confirmModal({
    showDeleteText: true,
    accept: () => void deleteFilter(filter.id),
  });
}

async function deleteFilter(id: number) {
  deletingId.value = id;
  try {
    await remove({ id });
    if (editingFilter.value?.id === id) {
      resetForm();
    }
  } finally {
    deletingId.value = null;
  }
}
</script>

<style scoped lang="scss">
.saved-filter-manager {
  width: min(32rem, calc(100vw - 2rem));
  padding: 1rem;
  font-size: var(--eab-text-sm, 0.875rem);

  &__list {
    display: grid;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  &__group + &__group {
    padding-top: 0.75rem;
    border-top: 1px solid var(--lisa-secondary);
  }

  &__group-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding-bottom: 0.25rem;
    font-size: var(--eab-text-base, 1rem);
    font-weight: 700;
  }

  &__row {
    display: grid;
    grid-template-columns: 1rem 3rem minmax(0, 1fr) auto;
    gap: 0.5rem;
    align-items: center;
    padding: 0.625rem 0;
  }

  &__drag-handle {
    color: var(--p-text-muted-color);
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &__badge {
    display: grid;
    min-height: 2.5rem;
    place-items: center;
    padding: 0.25rem;
    color: var(--p-primary-700);
    font-weight: 700;
    background: var(--p-primary-100);
    border-radius: 0.5rem;
  }

  &__summary {
    display: grid;
    min-width: 0;

    strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__row-actions {
    display: flex;
    gap: 0.375rem;
    align-items: center;
  }

  &--compact {
    width: min(40rem, calc(100vw - 2rem));
    padding: 0.75rem;

    .saved-filter-manager__list {
      max-height: min(24rem, 50vh);
      margin-bottom: 0.75rem;
      overflow-y: auto;
    }

    .saved-filter-manager__row {
      grid-template-columns: 1rem 2.5rem minmax(8rem, 1fr) auto;
      padding: 0.5rem 0;
    }

    .saved-filter-manager__badge {
      min-height: 2.5rem;
      color: var(--lisa-primary-foreground);
      background: var(--lisa-primary);
    }

    .saved-filter-manager__drag-handle {
      color: var(--lisa-muted-foreground);
    }
  }

  &__form {
    display: grid;
    gap: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--lisa-secondary);
  }

  &__fields {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
  }

  &__scope,
  &__actions {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  &__scope label {
    display: flex;
    gap: 0.375rem;
    align-items: center;
  }

  &__actions {
    justify-content: flex-end;
  }
}

@media (max-width: 36rem) {
  .saved-filter-manager--compact {
    .saved-filter-manager__row {
      grid-template-columns: 1rem 2.5rem minmax(0, 1fr);
    }

    .saved-filter-manager__row-actions {
      grid-column: 2 / -1;
      justify-content: flex-end;
    }
  }
}
</style>
